// v3.15-926: Public booking page — no auth required.
// URL: /book/:repSlug  (e.g. /book/adris-davis)
// v3.15-927: Phase 3 UX upgrades:
//   * Big welcome banner with the rep's full name
//   * Next 7 days shown as day tabs (with slot counts) instead of a date picker
//   * "Show more dates" reveals an additional 7 days
//   * Slots grouped into Morning / Afternoon / Evening
//   * Backend auto-sets status=confirmed on successful booking
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar as CalendarIcon, CheckCircle, Loader2, Sparkles, Clock, Sunrise, Sun, Moon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BASE = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BASE}/api`, headers: { 'Content-Type': 'application/json' } });

const toLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtTime = (iso, tzName) => {
  const opts = { hour: 'numeric', minute: '2-digit', hour12: true };
  if (tzName) opts.timeZone = tzName;
  return new Date(iso).toLocaleTimeString('en-US', opts);
};

// v3.15-928: the public page displays slots in the VIEWER's timezone
// (what the customer actually needs). We show a label so there's no
// ambiguity — "Times shown in your local time (Eastern Time)" etc.
// v3.15-933: a toggle now lets the user flip slots over to the rep's
// timezone so multi-TZ bookings stay unambiguous.
const browserTzName = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; }
  catch { return ''; }
})();
const browserTzAbbr = (() => {
  try {
    const p = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(new Date());
    const tzPart = p.find(x => x.type === 'timeZoneName');
    return tzPart ? tzPart.value : '';
  } catch { return ''; }
})();

const tzAbbrFor = (tzName, ref = new Date()) => {
  if (!tzName) return '';
  try {
    const p = new Intl.DateTimeFormat('en-US', { timeZone: tzName, timeZoneName: 'short' }).formatToParts(ref);
    const tzPart = p.find(x => x.type === 'timeZoneName');
    return tzPart ? tzPart.value : '';
  } catch { return ''; }
};

const dayLabel = (d) => ({
  weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
  dayNum:  d.getDate(),
  monthShort: d.toLocaleDateString('en-US', { month: 'short' }),
});

// v3.15-927: group slots by time of day.
// v3.15-933: when a rep timezone is selected, bucket on the rep's local hour
// (so a 7 PM Eastern slot lands in "Evening" even when viewed by a Pacific
// browser).
const bucketForSlot = (isoStart, tzName) => {
  let h;
  if (tzName) {
    try {
      const p = new Intl.DateTimeFormat('en-US', {
        timeZone: tzName, hour: 'numeric', hour12: false,
      }).formatToParts(new Date(isoStart));
      const hourPart = p.find((x) => x.type === 'hour');
      h = hourPart ? parseInt(hourPart.value, 10) : new Date(isoStart).getHours();
    } catch {
      h = new Date(isoStart).getHours();
    }
  } else {
    h = new Date(isoStart).getHours();
  }
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
};

const BUCKETS = [
  { key: 'morning',   label: 'Morning',   icon: Sunrise, hint: 'Before 12 PM' },
  { key: 'afternoon', label: 'Afternoon', icon: Sun,     hint: '12 PM – 5 PM' },
  { key: 'evening',   label: 'Evening',   icon: Moon,    hint: 'After 5 PM' },
];

export default function PublicBookingPage() {
  const { repSlug } = useParams();
  const [rep, setRep] = useState(null);
  const [loadingRep, setLoadingRep] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Day-tab state ------------------------------------------------------------
  const [daysToShow, setDaysToShow] = useState(7);   // 7 → 14 on "Show more"
  const [activeDate, setActiveDate] = useState(toLocalYMD(new Date()));
  // Cache of date → slots so switching tabs is instant after initial fetch.
  const [slotsByDate, setSlotsByDate] = useState({});
  const [loadingDates, setLoadingDates] = useState(() => new Set());

  const [selectedSlot, setSelectedSlot] = useState(null);

  // v3.15-933: TZ display toggle — when ON, render slots and confirmation
  // times in the rep's timezone instead of the viewer's local timezone. The
  // underlying slot ISO strings are unchanged; only the display formatter
  // takes a `timeZone` option.
  const [showInRepTz, setShowInRepTz] = useState(false);
  const repTzName = rep?.settings?.timezone || '';
  const repTzAbbr = useMemo(() => tzAbbrFor(repTzName), [repTzName]);
  const displayTz = showInRepTz && repTzName ? repTzName : '';
  const displayTzLabel = showInRepTz && repTzName
    ? `${rep?.name?.split(' ')[0] || 'rep'}'s time${repTzAbbr ? ` (${repTzAbbr})` : ''}`
    : `your local time${browserTzAbbr ? ` (${browserTzAbbr})` : ''}`;

  const [fullName, setFullName] = useState('');
  const [email,    setEmail]    = useState('');
  const [phone,    setPhone]    = useState('');
  const [notes,    setNotes]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmed,  setConfirmed]  = useState(null);   // {starts, ends, rep_name}

  // Build the list of dates to render as tabs.
  const dateList = useMemo(() => {
    const out = [];
    const today = new Date();
    for (let i = 0; i < daysToShow; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      out.push(toLocalYMD(d));
    }
    return out;
  }, [daysToShow]);

  // Fetch rep info once.
  useEffect(() => {
    if (!repSlug) return;
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/appointments/rep/${repSlug}`);
        setRep(data?.rep);
      } catch {
        setNotFound(true);
      } finally {
        setLoadingRep(false);
      }
    })();
  }, [repSlug]);

  // Fetch slots for each day in the visible list (skip any already cached).
  useEffect(() => {
    if (!repSlug || !rep) return;
    let alive = true;
    const toFetch = dateList.filter((d) => slotsByDate[d] === undefined);
    if (toFetch.length === 0) return;
    setLoadingDates((prev) => {
      const next = new Set(prev);
      toFetch.forEach((d) => next.add(d));
      return next;
    });
    (async () => {
      await Promise.all(toFetch.map(async (d) => {
        try {
          const { data } = await publicApi.get(`/public/appointments/rep/${repSlug}/availability`, { params: { date: d } });
          if (!alive) return;
          setSlotsByDate((prev) => ({ ...prev, [d]: data?.slots || [] }));
        } catch {
          if (!alive) return;
          setSlotsByDate((prev) => ({ ...prev, [d]: [] }));
        } finally {
          setLoadingDates((prev) => {
            const next = new Set(prev);
            next.delete(d);
            return next;
          });
        }
      }));
    })();
    return () => { alive = false; };
  }, [repSlug, rep, dateList]);

  const activeSlots  = slotsByDate[activeDate] || [];
  const loadingSlots = loadingDates.has(activeDate) && activeSlots.length === 0;

  const groupedSlots = useMemo(() => {
    const groups = { morning: [], afternoon: [], evening: [] };
    activeSlots.forEach((s) => { groups[bucketForSlot(s.start, displayTz)].push(s); });
    return groups;
  }, [activeSlots, displayTz]);

  const submit = async () => {
    if (!selectedSlot) { toast.error('Please select a time slot'); return; }
    if (!fullName.trim() || !email.trim() || !phone.trim()) {
      toast.error('Name, email, and phone are required'); return;
    }
    setSubmitting(true);
    try {
      const { data } = await publicApi.post('/public/appointments/book', {
        rep_slug: repSlug,
        date_time_start: selectedSlot.start,
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        notes: notes.trim(),
      });
      setConfirmed({ starts: data.starts, ends: data.ends, rep_name: data.rep_name });
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Booking failed — please try another time');
    } finally {
      setSubmitting(false);
    }
  };

  // ------- render -------
  if (loadingRep) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…</div>;
  }
  if (notFound || !rep) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-md text-center shadow-sm">
          <div className="text-lg font-semibold text-slate-800 mb-2">Booking link not found</div>
          <p className="text-sm text-slate-500">This booking link is invalid or no longer active. Please check with your representative for an updated link.</p>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-md text-center shadow-sm" data-testid="booking-confirmation">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <div className="text-lg font-bold text-slate-900 mb-1">Booking Confirmed</div>
          <p className="text-sm text-slate-600 mb-4">
            A confirmation email has been sent to <span className="font-medium">{email}</span>.
          </p>
          <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded px-4 py-3 text-left space-y-1">
            <div><span className="text-slate-500">When:</span> {new Date(confirmed.starts).toLocaleString('en-US', { weekday:'short', month:'short', day:'numeric', hour:'numeric', minute:'2-digit', ...(displayTz ? { timeZone: displayTz } : {}) })} {displayTzLabel ? <span className="text-[11px] text-slate-400">({displayTzLabel})</span> : null}</div>
            <div><span className="text-slate-500">With:</span> {confirmed.rep_name}</div>
          </div>
        </div>
      </div>
    );
  }

  const repFirstName = (rep.name || '').split(' ')[0] || rep.name;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-10 px-4" data-testid="public-booking-page">
      <div className="max-w-2xl mx-auto">
        {/* Welcome banner */}
        <div className="bg-gradient-to-br from-indigo-600 to-indigo-500 text-white rounded-2xl p-6 mb-5 shadow-sm" data-testid="booking-welcome-banner">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4" />
            <span className="text-[11px] uppercase tracking-wider font-semibold opacity-90">Book an appointment</span>
          </div>
          <h1 className="text-2xl font-bold mb-1" data-testid="booking-rep-name">
            with {rep.name}
          </h1>
          <p className="text-sm text-indigo-100" data-testid="booking-welcome-message">
            Welcome! {repFirstName} is excited to connect with you. Pick a time below — it only takes a minute.
          </p>
          <div className="mt-3 flex items-center gap-3 text-[11px] text-indigo-100">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Duration: {rep.settings?.default_appointment_duration || 30} min</span>
            <span>·</span>
            <span data-testid="booking-viewer-timezone">Times shown in {displayTzLabel}</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          {/* Day tabs */}
          <div className="mb-4">
            <label className="text-xs font-semibold text-slate-700 block mb-2">Pick a day</label>
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="booking-day-tabs">
              {dateList.map((d) => {
                const dt = new Date(d + 'T12:00:00');
                const { weekday, dayNum, monthShort } = dayLabel(dt);
                const isActive = d === activeDate;
                const count = (slotsByDate[d] || []).length;
                const stillLoading = loadingDates.has(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => { setActiveDate(d); setSelectedSlot(null); }}
                    className={`flex flex-col items-center px-3 py-2 rounded-lg border min-w-[72px] transition ${
                      isActive ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                               : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-400'
                    }`}
                    data-testid={`booking-day-${d}`}
                  >
                    <span className={`text-[10px] uppercase tracking-wide ${isActive ? 'text-indigo-100' : 'text-slate-500'}`}>{weekday}</span>
                    <span className="text-lg font-bold leading-none my-0.5">{dayNum}</span>
                    <span className={`text-[9px] ${isActive ? 'text-indigo-100' : 'text-slate-500'}`}>{monthShort}</span>
                    <span className={`mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                      stillLoading ? 'bg-slate-100 text-slate-400' :
                      count > 0 ? (isActive ? 'bg-white/25 text-white' : 'bg-green-100 text-green-700')
                                : (isActive ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-400')
                    }`}>
                      {stillLoading ? '…' : count > 0 ? `${count} open` : 'Full'}
                    </span>
                  </button>
                );
              })}
            </div>
            {daysToShow < 14 && (
              <button
                type="button"
                onClick={() => setDaysToShow(14)}
                className="mt-2 text-[11px] text-indigo-600 font-medium hover:underline"
                data-testid="booking-show-more-dates"
              >
                Show more dates →
              </button>
            )}
          </div>

          {/* Slots grouped by time of day */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <label className="text-xs font-semibold text-slate-700">Available times</label>
              <div className="flex items-center gap-2">
                {/* v3.15-933: TZ toggle — viewer TZ ⇄ rep TZ */}
                {repTzName && repTzName !== browserTzName && (
                  <button
                    type="button"
                    onClick={() => setShowInRepTz((v) => !v)}
                    className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                      showInRepTz
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'bg-white border-slate-300 text-slate-600 hover:border-indigo-400'
                    }`}
                    data-testid="booking-tz-toggle"
                    title={showInRepTz
                      ? 'Switch back to your local time'
                      : `Convert to ${rep?.name?.split(' ')[0] || 'rep'}'s timezone${repTzAbbr ? ` (${repTzAbbr})` : ''}`}
                  >
                    {showInRepTz
                      ? `Showing in ${repTzAbbr || 'rep TZ'}`
                      : `Convert to ${repTzAbbr || 'rep'}'s timezone`}
                  </button>
                )}
                <span className="text-[10px] text-slate-400" data-testid="booking-local-tz-label">
                  Times shown in {displayTzLabel}
                </span>
              </div>
            </div>
            {loadingSlots ? (
              <div className="text-xs text-slate-400 py-4 flex items-center justify-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading slots…</div>
            ) : activeSlots.length === 0 ? (
              <div className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded" data-testid="booking-slots-empty">
                No open slots on this day. Try another date above.
              </div>
            ) : (
              <div className="space-y-3" data-testid="booking-slots-grouped">
                {BUCKETS.map(({ key, label, icon: Icon, hint }) => {
                  const list = groupedSlots[key] || [];
                  if (list.length === 0) return null;
                  return (
                    <div key={key} data-testid={`slot-bucket-${key}`}>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Icon className="h-3.5 w-3.5 text-indigo-500" />
                        <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">{label}</span>
                        <span className="text-[10px] text-slate-400">· {hint} · {list.length} open</span>
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                        {list.map((s) => (
                          <button
                            key={s.start}
                            type="button"
                            onClick={() => setSelectedSlot(s)}
                            className={`h-9 text-xs rounded-md border transition ${selectedSlot?.start === s.start ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-200 hover:border-indigo-400 text-slate-700'}`}
                            data-testid={`booking-slot-${s.start.slice(11,16).replace(':','')}`}
                          >
                            {fmtTime(s.start, displayTz)}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {selectedSlot && (
            <div className="space-y-3 pt-3 border-t border-slate-100" data-testid="booking-details-form">
              <div>
                <label className="text-[11px] font-medium text-slate-600 block mb-1">Full name *</label>
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="booking-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-medium text-slate-600 block mb-1">Email *</label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="booking-email" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-600 block mb-1">Phone *</label>
                  <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="booking-phone" />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-600 block mb-1">Notes (optional)</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="booking-notes" />
              </div>
              <Button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="w-full bg-indigo-600 hover:bg-indigo-500"
                data-testid="booking-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirm Booking — {fmtTime(selectedSlot.start, displayTz)}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
